<template>
  <div class="outsource-detail">
    <Loader
      v-if="loaderEnabled"
      :logo="loaderLogo"
    />
    <KTCodePreview :title="title">
      <template v-slot:toolbar>
        <div
          v-if="detail && detail.status === WARRANTY_OUTSOURCE_STATUS.SENT"
          class="row"
        >
          <b-row
            class="mb-0 ml-4"
            style="vertical-align: middle; display: inline"
          >
            <EditToggle
              v-model="isEditMode"
              id="customSwitchOutsource"
              label="Chỉnh sửa"
            />
          </b-row>
        </div>
      </template>
      <template v-slot:preview>
        <LoadingSpinner
          v-if="isLoading"
          message="Đang tải..."
        />
        <template v-else-if="detail">
          <!-- Block 1: Thông tin máy (từ Warranty) -->
          <div class="border rounded p-3 mb-3">
            <h6 class="font-weight-bold mb-3">Thông tin máy</h6>
            <b-row>
              <b-col md="6">
                <p class="mb-1">
                  <b>Mã phiếu bảo hành: </b>
                  <span class="text-primary font-weight-bold hover-link">{{ warrantyCode }}</span>
                </p>
                <p class="mb-1">
                  <b>Tên máy:</b>
                  {{ detail.productName || (detail.warranty && detail.warranty.productName) }}
                </p>
                <p class="mb-1">
                  <b>IMEI:</b>
                  {{ detail.imeiCode || (detail.warranty && detail.warranty.imeiCode) }}
                </p>
              </b-col>
              <b-col md="6">
                <p class="mb-1"><b>Khách hàng:</b> {{ customerName }}</p>
                <p class="mb-1"><b>SĐT:</b> {{ customerPhone }}</p>
                <p class="mb-1">
                  <b>Lỗi mô tả ban đầu:</b> {{ warrantyIssueSummary }}
                </p>
              </b-col>
            </b-row>
          </div>
          <!-- Block 2: Thông tin outsource -->
          <div class="border rounded p-3 mb-3">
            <h6 class="font-weight-bold mb-3">Thông tin outsource</h6>
            <b-row v-if="!isEditMode">
              <b-col md="6">
                <p class="mb-1">
                  <b>Mã phiếu outsource: </b>
                  <span class="text-primary font-weight-bold">{{ detail.code }}</span>
                </p>
                <p class="mb-1">
                  <b>Trạng thái:</b>
                  <OutsourceStatusBadge
                    :status="detail.status"
                    :status-name="detail.statusName"
                  />
                </p>
                <p class="mb-1"><b>Nhà cung cấp:</b> {{ detail.providerName }}</p>
                <p class="mb-1">
                  <b>Ngày gửi:</b> {{ convertToDateSlash(detail.sentDate, DATE_FORMAT) }}
                </p>
                <p class="mb-1">
                  <b>Ngày hẹn trả:</b>
                  {{ convertToDateSlash(detail.expectedReceiveDate, DATE_FORMAT) }}
                </p>
                <p
                  class="mb-1"
                >
                  <b>Chi phí dự kiến:</b> {{ convertPrice(detail.estimatedCost) }}
                </p>
              </b-col>
              <b-col md="6">
                <p class="mb-1"><b>Lý do outsource / mô tả lỗi:</b></p>
                <p class="text-muted">{{ detail.issueDescription }}</p>
                <p class="mb-1"><b>Người gửi:</b> {{ detail.sentByEmployeeName }}</p>
              </b-col>
            </b-row>
            <b-row v-else>
              <b-col cols="6">
                <b-form-group label="Ngày hẹn trả">
                  <date-picker
                    v-model="editForm.expectedReceiveDate"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                  />
                </b-form-group>
                <b-form-group label="Chi phí dự kiến">
                  <BInputNumber
                    :value="editForm.estimatedCost"
                    @valueChange="(v) => (editForm.estimatedCost = v)"
                    size="sm"
                  />
                </b-form-group>
                <b-form-group label="Lý do outsource / mô tả lỗi">
                  <b-form-textarea
                    v-model="editForm.issueDescription"
                    rows="3"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>
          <!-- Block 3: Thông tin nhận về (khi RECEIVED) -->
          <div
            v-if="detail.status === WARRANTY_OUTSOURCE_STATUS.RECEIVED"
            class="border rounded p-3 mb-3"
          >
            <h6 class="font-weight-bold mb-3">Thông tin nhận về</h6>
            <b-row>
              <b-col md="6">
                <p class="mb-1">
                  <b>Ngày nhận thực tế:</b>
                  {{ convertToDateSlash(detail.actualReceiveDate, DATE_FORMAT) }}
                </p>
                <p class="mb-1 text-left"><b>Chi phí thực tế:</b> {{ convertPrice(detail.actualCost) }}</p>
              </b-col>
              <b-col md="6">
                <p class="mb-1"><b>Ghi chú nhận hàng / kết quả sửa:</b></p>
                <p class="text-muted">{{ detail.note || '—' }}</p>
                <p class="mb-1"><b>Người nhận:</b> {{ detail.receivedByEmployeeName }}</p>
              </b-col>
            </b-row>
          </div>
          <!-- Block 4: Lịch sử outsource của máy -->
          <div
            v-if="detail && detail.warrantyId && !isEditMode"
            class="border rounded p-3"
          >
            <h6 class="font-weight-bold mb-3">Lịch sử outsource của máy</h6>
            <b-table
              small
              :items="historyList"
              :fields="historyFields"
              :busy="historyLoading"
              bordered
              show-empty
              empty-text="Chưa có dữ liệu"
            >
              <template #cell(code)="row"><a href="#" @click.stop="linkToWarrantyOutsource(row.item)">{{ row.item.code }}</a></template>
              <template #cell(providerName)="row">{{ row.item.providerName }}</template>
              <template #cell(sentDate)="row">{{
                convertToDateSlash(row.item.sentDate, DATE_FORMAT)
              }}</template>
              <template #cell(actualReceiveDate)="row">{{
                convertToDateSlash(row.item.actualReceiveDate, DATE_FORMAT)
              }}</template>
              <template #cell(actualCost)="row">{{ convertPrice(row.item.actualCost) }}</template>
              <template #cell(estimatedCost)="row">{{ convertPrice(row.item.estimatedCost) }}</template>
              <template #cell(status)="row">
                <OutsourceStatusBadge
                  :status="row.item.status"
                  :status-name="row.item.statusName"
                />
              </template>
            </b-table>
            <div
              v-if="historyTotalRow > 0"
              class="d-flex justify-content-between align-items-center mt-2"
            >
              <span class="font-weight-bold"> Tổng {{ historyTotalRow }} bản ghi </span>
              <BPaginationNavCustom
                v-if="historyTotalPage > 1"
                :number-of-pages="historyTotalPage"
                :current-page="historyPage"
                :total-page="historyTotalPage"
                @page-change="onHistoryPageChange"
              />
            </div>
          </div>
          <div class="mt-4 pt-3 border-top">
            <SaveButton
              v-if="isEditMode"
              :is-busy="isSaving"
              text="Lưu"
              variant="primary"
              size="sm"
              button-class="mr-2"
              @click="saveEdit"
            />
            <b-button
              v-if="detail && detail.status === WARRANTY_OUTSOURCE_STATUS.SENT"
              size="sm"
              variant="success"
              class="mr-2"
              @click="openReceiveModal"
            >
              Nhận hàng
            </b-button>
            <b-button
              size="sm"
              variant="secondary"
              @click="goBack"
            >
              Quay lại
            </b-button>
          </div>
        </template>
        <p
          v-else-if="!isLoading"
          class="text-muted"
        >
          Không tìm thấy phiếu outsource.
        </p>
      </template>
    </KTCodePreview>
    <ReceiveOutsourceModal
      ref="receiveModalRef"
      :item="detail"
      @done="fetchDetail"
    />
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue'
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module'
import { mapGetters } from 'vuex'
import Loader from '@/view/content/Loader.vue'
import moment from 'moment'
import timeUtils, { convertToDateSlash, DATE_FORMAT } from '@/utils/date'
import { convertPrice } from '@/utils/common'
import { WARRANTY_OUTSOURCE_STATUS } from '@/utils/enum'
import { getOutsourceById, getOutsourceList, updateOutsource } from './warrantyOutsourceApi'
import OutsourceStatusBadge from './components/OutsourceStatusBadge.vue'
import ReceiveOutsourceModal from './components/ReceiveOutsourceModal.vue'
import BInputNumber from '@/view/base/bootstrap/BInputNumber.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import SaveButton from '@/view/components/common/SaveButton.vue'
import EditToggle from '@/view/components/common/EditToggle.vue'
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue'

export default {
  name: 'OutsourceDetail',
  components: {
    KTCodePreview,
    Loader,
    OutsourceStatusBadge,
    ReceiveOutsourceModal,
    BInputNumber,
    LoadingSpinner,
    SaveButton,
    EditToggle,
    BPaginationNavCustom,
  },
  data() {
    return {
      DATE_FORMAT,
      WARRANTY_OUTSOURCE_STATUS,
      detail: null,
      historyList: [],
      historyPage: 1,
      historyLimit: 10,
      historyTotalRow: 0,
      historyTotalPage: 1,
      historyLoading: false,
      isLoading: false,
      isSaving: false,
      isEditMode: false,
      dpConfigs: timeUtils.DP_CONFIG,
      editForm: {},
      historyFields: [
        { key: 'code', label: 'Mã phiếu', thClass: 'text-center' },
        { key: 'providerName', label: 'Nhà cung cấp', thClass: 'text-center' },
        { key: 'sentDate', label: 'Ngày gửi', tdClass: 'text-center', thClass: 'text-center' },
        {
          key: 'actualReceiveDate',
          label: 'Ngày nhận',
          tdClass: 'text-center',
          thClass: 'text-center',
        },
        { key: 'estimatedCost', label: 'Chi phí dự kiến', tdClass: 'text-right', thClass: 'text-right' },
        { key: 'actualCost', label: 'Chi phí thực tế', tdClass: 'text-right', thClass: 'text-right' },
        { key: 'status', label: 'Trạng thái', tdClass: 'text-center', thClass: 'text-center' },
      ],
    }
  },
  computed: {
    ...mapGetters(['layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'))
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo')
    },
    title() {
      return this.detail ? `Chi tiết outsource #${this.detail.id}` : 'Chi tiết phiếu outsource'
    },
    warrantyCode() {
      return (
        this.detail?.warrantyCode || (this.detail?.warranty && this.detail.warranty.code) || '—'
      )
    },
    customerName() {
      return (
        this.detail?.customerName ||
        (this.detail?.warranty && this.detail.warranty.customerName) ||
        '—'
      )
    },
    customerPhone() {
      return (
        this.detail?.customerPhone ||
        (this.detail?.warranty && this.detail.warranty.customerPhone) ||
        '—'
      )
    },
    warrantyIssueSummary() {
      const w = this.detail?.warranty
      if (!w) return '—'
      const latest = Array.isArray(w.warrantyRepairs) && w.warrantyRepairs.length ? w.warrantyRepairs[0] : null
      return latest?.repairSolution || w.issueDescription || '—'
    },
  },
  watch: {
    '$route.query.id'() {
      this.fetchDetail()
    },
  },
  mounted() {
    this.fetchDetail()
  },
  methods: {
    linkToWarrantyOutsource(item) {
      const route = this.$router.resolve({ name: 'warranty-dispatch-info', query: { id: item.warrantyId } })
      window.open(route.href, '_blank')
    },
    convertPrice,
    convertToDateSlash,
    async fetchDetail() {
      const id = this.$route.query.id
      if (!id) {
        this.detail = null
        return
      }
      this.isLoading = true
      try {
        this.detail = await getOutsourceById(id)
        this.$store.dispatch(SET_BREADCRUMB, [
          { title: 'Outsource', to: { name: 'warranty-outsource-list' } },
          { title: this.title },
        ])
        if (this.detail && this.detail.warrantyId) {
          this.historyPage = 1
          await this.fetchHistory(1)
        } else {
          this.historyList = this.detail ? [this.detail] : []
          this.historyTotalRow = this.detail ? 1 : 0
          this.historyTotalPage = 1
        }
        const raw = this.detail?.expectedReceiveDate
        this.editForm = {
          expectedReceiveDate: raw ? (moment(raw).isValid() ? moment(raw).toDate() : raw) : null,
          estimatedCost: this.detail?.estimatedCost,
          issueDescription: this.detail?.issueDescription,
        }
      } catch (e) {
        this.detail = null
        this.historyList = []
        this.historyTotalRow = 0
        this.historyTotalPage = 1
      } finally {
        this.isLoading = false
      }
    },
    getStatusName(s) {
      const map = { SENT: 'Đã xuất', RECEIVED: 'Đã nhận về', CANCELLED: 'Đã huỷ' }
      return map[s] || s
    },
    async fetchHistory(page) {
      if (!this.detail?.warrantyId) return
      this.historyLoading = true
      try {
        const res = await getOutsourceList({
          imeiCode: this.detail.warranty?.imeiCode,
          page: Number(page) || 1,
          limit: this.historyLimit,
        })
        this.historyTotalRow = res.totalRow || 0
        this.historyTotalPage = Math.max(1, res.totalPage || 1)
        this.historyList = (res.dataset || []).map((r) => ({
          ...r,
          statusName: r.statusName || this.getStatusName(r.status),
        }))
      } catch {
        this.historyList = []
        this.historyTotalRow = 0
        this.historyTotalPage = 1
      } finally {
        this.historyLoading = false
      }
    },
    onHistoryPageChange(page) {
      this.historyPage = Number(page) || 1
      this.fetchHistory(this.historyPage)
    },
    openReceiveModal() {
      this.$refs.receiveModalRef?.show()
    },
    async saveEdit() {
      if (this.isSaving) return
      this.isSaving = true
      try {
        const d = this.editForm.expectedReceiveDate
        const expectedReceiveDate = d
          ? timeUtils.formatDate(d, timeUtils.YEAR_MONTH_DAY_FORMAT)
          : null
        await updateOutsource(this.detail.id, {
          expectedReceiveDate,
          estimatedCost:
            this.editForm.estimatedCost != null && this.editForm.estimatedCost !== ''
              ? Number(this.editForm.estimatedCost)
              : null,
          issueDescription: this.editForm.issueDescription?.trim() || null,
        })
        this.isEditMode = false
        this.fetchDetail()
      } catch {
      } finally {
        this.isSaving = false
      }
    },
    goBack() {
      this.$router.push({ name: 'warranty-outsource-list' })
    },
  },
}
</script>

<style scoped>
.outsource-detail .table th,
.outsource-detail .table td {
  padding: 0.5rem;
}
</style>
